import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { ApiConstant, KeyConstant } from "@/const";
import { HumpsUtils, StorageUtils } from "@/utils";
import { SERVER_TYPE } from "@/const/system.const";

/**
 * Api logic base on Axios
 */
export class GlobalApiGenerator {
  protected instance: AxiosInstance;

  public constructor(serverType: SERVER_TYPE = SERVER_TYPE.SGW) {
    let baseURL = ApiConstant.BASE_URL;
    if (serverType == SERVER_TYPE.DATALAKE) {
      baseURL = ApiConstant.DATALAKE_URL;
    }

    if (serverType == SERVER_TYPE.GST) {
      baseURL = ApiConstant.GST_URL;
    }

    if (serverType == SERVER_TYPE.SAILOR_ID) {
      baseURL = ApiConstant.SAILOR_URL;
    }
    const timeout = ApiConstant.TIMEOUT;
    this.instance = axios.create({ baseURL: baseURL, timeout: timeout });
    this.default();
  }

  /**
   * Handle default request success interceptor
   *
   * @param {InternalAxiosRequestConfig} configs
   * @returns configs
   */
  protected handleDefaultRequestSuccess<C>(configs: InternalAxiosRequestConfig<C>) {
    const newToken = StorageUtils.getItem(KeyConstant.KEY_TOKEN);
    if (newToken) configs.headers.Authorization = `Bearer ${newToken}`;
    return configs;
  }

  /**
   * Handle default request error interceptor
   *
   * @param {any} error
   * @returns error
   */
  protected handleDefaultRequestError<C>(error: C) {
    return Promise.reject(error);
  }

  /**
   * Handle default response success interceptor
   *
   * @param {AxiosResponse} response
   * @returns response
   */
  protected handleDefaultResponseSuccess(response: AxiosResponse) {
    return response;
  }

  /**
   * Handle get attachment response success interceptor
   *
   * @param {AxiosResponse} response
   * @returns response
   */
  protected handleToCamelResponseSuccess(response: AxiosResponse) {
    response.data = typeof response.data === "object" ? HumpsUtils.camelizeKeys(response.data) : response.data;
    return response;
  }

  /**
   * Handle default response error interceptor
   *
   * @param {any} error
   * @returns error
   */
  protected handleDefaultResponseError(error: any) {
    if (error.response && error.response.status === ApiConstant.HTTP_STATUS.STT_UNAUTHORIZED) {
      StorageUtils.clear();
    }
    return Promise.reject(error);
  }

  /**
   * Common API request logic
   *
   * @param url
   * @param method
   * @param params
   * @param data
   * @param configs
   * @returns response
   */
  protected async request<T, D>(
    url: string,
    method: string,
    params: any = {},
    data: any = {},
    configs: AxiosRequestConfig = {},
  ): Promise<GlobalApiResponseType<T, D>> {
    const requestConfigs: AxiosRequestConfig = { ...configs, url, method, params, data };
    const response: any = await this.instance.request(requestConfigs);
    if (response.status === ApiConstant.HTTP_STATUS.STT_OK) response.ok = true;
    return response;
  }

  /**
   * Request with GET method
   *
   * @param url
   * @param params
   * @param configs
   * @returns response
   */
  public async get<T, D = any>(
    url: string,
    params: any = {},
    configs: AxiosRequestConfig = {},
  ): Promise<GlobalApiResponseType<T, D>> {
    return this.request(url, "get", params, {}, configs);
  }

  /**
   * Request with POST method
   *
   * @param url
   * @param data
   * @param configs
   * @returns response
   */
  public async post<T, D = any>(
    url: string,
    data: any = {},
    configs: AxiosRequestConfig = {},
  ): Promise<GlobalApiResponseType<T, D>> {
    return this.request(url, "post", {}, data, configs);
  }

  /**
   * Request with PUT method
   *
   * @param url
   * @param data
   * @param configs
   * @returns response
   */
  public async put<T, D = any>(
    url: string,
    data: any = {},
    configs: AxiosRequestConfig = {},
  ): Promise<GlobalApiResponseType<T, D>> {
    return this.request(url, "put", {}, data, configs);
  }

  /**
   * Request with PATCH method
   *
   * @param url
   * @param data
   * @param configs
   * @returns response
   */
  public async patch<T, D = any>(
    url: string,
    data: any = {},
    configs: AxiosRequestConfig = {},
  ): Promise<GlobalApiResponseType<T, D>> {
    return this.request(url, "patch", {}, data, configs);
  }

  /**
   * Request with DELETE method
   *
   * @param url
   * @param data
   * @param configs
   * @returns response
   */
  public async delete<T, D = any>(
    url: string,
    data: any = {},
    configs: AxiosRequestConfig = {},
  ): Promise<GlobalApiResponseType<T, D>> {
    return this.request(url, "delete", {}, data, configs);
  }

  /**
   * Default interceptors
   *
   * @returns this
   */
  public toCamel() {
    this.instance.interceptors.request.clear();
    this.instance.interceptors.response.clear();
    this.instance.interceptors.request.use(
      configs => this.handleDefaultRequestSuccess(configs),
      error => this.handleDefaultRequestError(error),
    );
    this.instance.interceptors.response.use(
      response => this.handleToCamelResponseSuccess(response),
      error => this.handleDefaultResponseError(error),
    );
    return this;
  }

  /**
   * Get attachment interceptors
   *
   * @returns this
   */
  public default() {
    this.instance.interceptors.request.clear();
    this.instance.interceptors.response.clear();
    this.instance.interceptors.request.use(
      configs => this.handleDefaultRequestSuccess(configs),
      error => this.handleDefaultRequestError(error),
    );
    this.instance.interceptors.response.use(
      response => this.handleDefaultResponseSuccess(response),
      error => this.handleDefaultResponseError(error),
    );
    return this;
  }
}

const GlobalApi = new GlobalApiGenerator();

export const DatalakeApi = new GlobalApiGenerator(SERVER_TYPE.DATALAKE);

export const GSTApi = new GlobalApiGenerator(SERVER_TYPE.GST);

export const SailorIdApi = new GlobalApiGenerator(SERVER_TYPE.SAILOR_ID);

export default GlobalApi;

export type GlobalApiResponseType<T, D> = AxiosResponse<T, D> & {
  ok?: boolean;
};

export type ApiResponse<T> = {
  data: T;
};
